// SPDX-License-Identifier: AGPL-3.0-or-later

import {AMBIENCE_DUCK_GAIN, loadRoomSound, type RoomSoundSource} from '@app/features/voice_scene/audio/RoomAmbience';
import {
	FLAT_SPEAKER_PARAMS,
	listenerForward,
	type SpatialListener,
	toListenerFrame,
} from '@app/features/voice_scene/audio/SpatialAudioMath';
import type {Vec3} from '@app/features/voice_scene/scene/VoiceSceneTypes';

const RAMP_TIME_CONSTANT_S = 0.15;
// Below these a new place changes nothing worth a ramp.
const LEVEL_EPSILON = 1e-4;
const POSITION_EPSILON_M = 0.01;

export interface RoomEmitterSound extends RoomSoundSource {
	refDistance: number;
	maxDistance: number;
}

// position: listener-relative, as the voices (in front with 3D audio off); distance: to the listener's ears.
export interface RoomSoundPlace {
	position: Vec3;
	distance: number;
}

export function distanceLevel(distance: number, refDistance: number, maxDistance: number): number {
	if (distance <= refDistance) return 1;
	if (distance >= maxDistance) return 0;
	return 1 - (distance - refDistance) / (maxDistance - refDistance);
}

export function soundPlace(point: Vec3, listener: SpatialListener, spatial: boolean): RoomSoundPlace {
	const {position} = listener;
	return {
		position: spatial ? toListenerFrame(point, listener, listenerForward(listener)) : FLAT_SPEAKER_PARAMS.position,
		distance: Math.hypot(point.x - position.x, point.y - position.y, point.z - position.z),
	};
}

export interface RoomEmitters {
	// One place per sound, in order; null: its node is not in this room, it stays silent.
	place(places: ReadonlyArray<RoomSoundPlace | null>): void;
	// 0 to 1, the ambience's volume.
	setVolume(volume: number): void;
	setDucked(ducked: boolean): void;
	setDeafened(deafened: boolean): void;
	dispose(): void;
}

interface Chain {
	sound: RoomEmitterSound;
	gain: GainNode;
	panner: PannerNode;
	source: AudioBufferSourceNode | null;
	loading: boolean;
	place: RoomSoundPlace | null;
	level: number;
}

// The room's sounds at a point (a fire, a projector): per sound one looping source → gain → equal-power panner at its
// place → the shared context's destination, on the ambience's volume, ducking and deafen. A file is fetched only once
// its sound is audible; the panners keep rolloffFactor 0, the level is distanceLevel.
export function createRoomEmitters({
	context,
	sounds,
	volume: initialVolume,
}: {
	context: AudioContext;
	sounds: ReadonlyArray<RoomEmitterSound>;
	volume: number;
}): RoomEmitters {
	const abort = new AbortController();
	let volume = initialVolume;
	let ducked = false;
	let deafened = false;

	const chains: Array<Chain> = sounds.map((sound) => {
		const gain = context.createGain();
		gain.gain.value = 0;
		const panner = context.createPanner();
		panner.panningModel = 'equalpower';
		panner.distanceModel = 'linear';
		panner.rolloffFactor = 0;
		panner.channelCount = 1;
		panner.channelCountMode = 'explicit';
		gain.connect(panner);
		panner.connect(context.destination);
		return {sound, gain, panner, source: null, loading: false, place: null, level: 0};
	});

	const start = (chain: Chain, buffer: AudioBuffer | null): void => {
		if (!buffer || abort.signal.aborted) return;
		const source = context.createBufferSource();
		source.buffer = buffer;
		source.loop = true;
		source.connect(chain.gain);
		source.start();
		chain.source = source;
	};
	const apply = (chain: Chain): void => {
		const {sound, place} = chain;
		const level =
			deafened || !place
				? 0
				: volume *
					sound.gain *
					(ducked ? AMBIENCE_DUCK_GAIN : 1) *
					distanceLevel(place.distance, sound.refDistance, sound.maxDistance);
		if (Math.abs(level - chain.level) > LEVEL_EPSILON) {
			chain.level = level;
			chain.gain.gain.setTargetAtTime(level, context.currentTime, RAMP_TIME_CONSTANT_S);
		}
		if (level > 0 && !chain.loading) {
			chain.loading = true;
			void loadRoomSound(context, sound.url, abort.signal).then((buffer) => start(chain, buffer));
		}
	};
	const move = (chain: Chain, next: RoomSoundPlace | null): void => {
		const before = chain.place?.position;
		chain.place = next;
		if (!next) return;
		const {x, y, z} = next.position;
		const moved =
			!before ||
			Math.abs(before.x - x) > POSITION_EPSILON_M ||
			Math.abs(before.y - y) > POSITION_EPSILON_M ||
			Math.abs(before.z - z) > POSITION_EPSILON_M;
		if (!moved) return;
		const now = context.currentTime;
		chain.panner.positionX.setTargetAtTime(x, now, RAMP_TIME_CONSTANT_S);
		chain.panner.positionY.setTargetAtTime(y, now, RAMP_TIME_CONSTANT_S);
		chain.panner.positionZ.setTargetAtTime(z, now, RAMP_TIME_CONSTANT_S);
	};

	return {
		place(places) {
			chains.forEach((chain, index) => {
				move(chain, places[index] ?? null);
				apply(chain);
			});
		},
		setVolume(next) {
			volume = next;
			chains.forEach(apply);
		},
		setDucked(next) {
			ducked = next;
			chains.forEach(apply);
		},
		setDeafened(next) {
			deafened = next;
			chains.forEach(apply);
		},
		dispose() {
			abort.abort();
			for (const chain of chains) {
				if (chain.source) {
					chain.source.stop();
					chain.source.disconnect();
					chain.source.buffer = null;
					chain.source = null;
				}
				chain.gain.disconnect();
				chain.panner.disconnect();
			}
		},
	};
}
